import classNames from "classnames"
import { isValidElement, PropsWithChildren, ComponentProps } from "react"
import { BASE_CLASSES, BASE_CLASSES_ACTIVE, BASE_CLASSES_FOCUS } from "../Button/constants"
import { Icon } from "../Icon"
import { BASE_CLASSES_SM } from "../IconButton/constants"
import { deepFind } from "../utils/deepFind"
import { validateDisplayNameChild } from "../utils/validateDisplayNameChild"
import { FOCUS_STATE_BASE_CLASSES } from "../utils/focusStateClasses"
import {
  AlertContext,
  ICON_STYLES_VARIATION,
  ICON_VARIATION,
  STYLES_VARIATION,
  useAlertContext,
} from "./utils"
import type { AlertProps, AlertContentProps } from "./types"

function AlertHeading({ children }: PropsWithChildren<unknown>) {
  return <div className="font-semibold">{children}</div>
}

AlertHeading.displayName = "Alert.Heading"

function AlertIcon() {
  const { variation } = useAlertContext()
  if (!variation) return null
  return (
    <div className="min-w-min">
      <Icon
        aria-hidden // hiding since the content should provide the context for the alert
        className={classNames("min-w-min", ICON_STYLES_VARIATION[variation])}
        data-testid="alert-icon"
        name={ICON_VARIATION[variation]}
        role="presentation"
        size="sm"
      />
    </div>
  )
}
AlertIcon.displayName = "Alert.Icon"

function AlertContent({ children, ...rest }: AlertContentProps) {
  return <div {...rest}>{children}</div>
}
AlertContent.displayName = "Alert.Content"

function AlertCloseButton({ ...rest }: Pick<ComponentProps<"button">, "aria-label" | "onClick">) {
  return (
    <button
      className={classNames(
        "ml-auto shrink-0 border-transparent bg-transparent",
        BASE_CLASSES,
        BASE_CLASSES_ACTIVE,
        BASE_CLASSES_FOCUS,
        FOCUS_STATE_BASE_CLASSES,
        BASE_CLASSES_SM,
      )}
      {...rest}
    >
      <Icon name="Times" size="sm" />
    </button>
  )
}
AlertCloseButton.displayName = "Alert.CloseButton"

export function Alert({
  children,
  variation = "gray",
  closeLabel = "Close",
  onRequestClose,
  className,
}: AlertProps) {
  const _className = classNames(
    "py-3 px-4 rounded-md flex gap-3",
    STYLES_VARIATION[variation],
    className,
  )

  const _icon = deepFind(children, child => {
    return isValidElement(child) && validateDisplayNameChild(child, "Alert.Icon")
  })
  const _heading = deepFind(children, child => {
    return isValidElement(child) && validateDisplayNameChild(child, "Alert.Heading")
  })
  const _content = deepFind(children, child => {
    return isValidElement(child) && validateDisplayNameChild(child, "Alert.Content")
  })

  return (
    <AlertContext.Provider value={{ variation }}>
      <div role="alert" className={_className}>
        {_icon}
        <div>
          {_heading}
          {_content}
        </div>
        {onRequestClose && <AlertCloseButton aria-label={closeLabel} onClick={onRequestClose} />}
      </div>
    </AlertContext.Provider>
  )
}

Alert.Heading = AlertHeading
Alert.Icon = AlertIcon
Alert.Content = AlertContent
